import ActiveUsersChart from '@/components/Analytics/ActiveUsersChart';
import GasConsumptionChart from '@/components/Analytics/GasConsumptionChart';
import OpenInterestChart from '@/components/Analytics/OpenInterestChart';
import RevenueChart from '@/components/Analytics/RevenueChart';
import TotalValueLockedChart from '@/components/Analytics/TotalValueLockedChart';
import TradingVolumeChart from '@/components/Analytics/TradingVolumeChart';
import TransactionsChart from '@/components/Analytics/TransactionsChart';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { Info } from 'lucide-react';
import React, { useMemo } from 'react';

// Exclusive upper bound — points on/before 2026-05-07 are kept; 2026-05-08+ is hidden.
const PERP_CUTOFF = new Date('2026-05-08T00:00:00Z');

function filterByCutoff<T extends { date: string }>(arr?: T[]): T[] | undefined {
  if (!arr) return undefined;
  return arr.filter(item => new Date(item.date) < PERP_CUTOFF);
}

const PerpsSection: React.FC = () => {
  const { data, loading } = useAnalytics();

  const filtered = useMemo(
    () => ({
      volume: filterByCutoff(data?.perp.volume?.data),
      openInterest: filterByCutoff(data?.perp.openInterest?.data),
      users: filterByCutoff(data?.perp.users?.data),
      tvl: filterByCutoff(data?.perp.tvl?.data),
      transactions: filterByCutoff(data?.perp.transactions?.data),
      revenue: filterByCutoff(data?.perp.revenue?.data),
      gasConsumption: filterByCutoff(data?.perp.gasConsumption?.data),
    }),
    [data]
  );

  return (
    <section>
      <div className="mb-6 flex items-center gap-2">
        <h2 className="text-xl font-semibold text-text-black">Perps</h2>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="About Perps section"
                className="inline-flex bg-transparent border-0 p-0 cursor-pointer text-text-main-body hover:text-text-black transition-colors"
              >
                <Info className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs leading-relaxed text-text-black">
                Perp markets settled on May 8, 2026 as part of the{' '}
                <a
                  href="https://monday.trade/post/perp-markets-are-transitioning"
                  target="_blank"
                  rel="noreferrer"
                  className="text-text-brand underline hover:opacity-80"
                >
                  transition
                </a>
                . Numbers below are preserved for reference and are not actively
                updated.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-text-main-body">Loading chart data...</div>
        </div>
      )}

      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TradingVolumeChart data={filtered.volume} />
          <OpenInterestChart data={filtered.openInterest} />
          <ActiveUsersChart data={filtered.users} />
          <TotalValueLockedChart data={filtered.tvl} />
          <TransactionsChart data={filtered.transactions} />
          <RevenueChart data={filtered.revenue} />
          <GasConsumptionChart data={filtered.gasConsumption} />
        </div>
      )}
    </section>
  );
};

export default PerpsSection;
